import { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import { Checkbox } from '@wartek-id/checkbox-v1'
import type { DialogProps } from '@wartek-id/dialog-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import { useAPIPostPelaksanaanAppealApproval } from 'api/kinerja/useAPIKinerja'
import { API_PATH_NOTIFICATION_LIST } from 'api/kinerja/useAPIKinerjaNotification'
import { assetUrl } from 'utils/url'
import { PRAKTIK_KINERJA_STEPS } from 'app/KinerjaGpkPage/constants/praktik-kinerja-steps'

import type { TPelaksanaanAppealableStep } from 'types/kinerja-pelaksanaan'

type PelaksanaanAppealRejectionDialogProps = Omit<DialogProps, 'children'> & {
  pegawaiName: string
  requestId?: string // identifier for rollback request
  step: TPelaksanaanAppealableStep
}

export function PelaksanaanAppealRejectionDialog({
  pegawaiName,
  requestId,
  isOpen,
  step,
  onClose,
  ...dialogProps
}: PelaksanaanAppealRejectionDialogProps) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [isConfirmed, setEnableIsConfirmed] = useState(false)

  // ----------------------------------------------------
  // Handle content state
  // ----------------------------------------------------
  const [activeContent, setActiveContent] = useState<'FORM' | 'SUCCESS'>('FORM')

  // ----------------------------------------------------
  // Handle mutation
  // ----------------------------------------------------
  const rollbackRequestMutation = useAPIPostPelaksanaanAppealApproval(requestId)

  const handleSubmit = () => {
    rollbackRequestMutation.mutate(
      { confirmationState: 'REJECTED', tipe: 'GPK' },
      {
        onSuccess: () => {
          setActiveContent('SUCCESS')
          queryClient.invalidateQueries(API_PATH_NOTIFICATION_LIST)
        },
        onError: () => {
          onClose()
          toast({ message: 'Gagal lanjut. Coba lagi.' })
        },
      }
    )
  }

  const isLoadingSubmission = rollbackRequestMutation.isLoading

  // ----------------------------------------------------
  // Reset dialog upon dismissal
  // ----------------------------------------------------
  useEffect(() => {
    if (!isOpen) {
      setEnableIsConfirmed(false)
      setActiveContent('FORM')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // ----------------------------------------------------
  // Resolve form name
  // ----------------------------------------------------
  const formName = PRAKTIK_KINERJA_STEPS[step]?.title ?? 'dokumen'

  return (
    <Dialog
      closeOnOverlayClick={false}
      isOpen={isOpen}
      size="sm"
      {...dialogProps}
    >
      <DialogOverlay />
      <DialogContent
        className="p-6"
        aria-labelledby="appeal-rejection-dialog-title"
        aria-describedby="appeal-rejection-dialog-description"
      >
        {activeContent === 'FORM' && (
          <>
            <Icon
              color="critical"
              className="!text-5xl mb-2"
              width={48}
              height={48}
            >
              warning
            </Icon>
            <Text
              variant="display-sm"
              className="mb-2"
              id="appeal-rejection-dialog-title"
              as="h3"
            >
              {`Tolak pengajuan perubahan isi ${formName} milik ${pegawaiName}?`}
            </Text>
            <Text color="subdued" variant="body-lg" className="mb-4">
              Status penolakan akan ditampilkan ke Pegawai dan Pegawai dapat
              mengajukan perubahan lagi.
            </Text>
            <Checkbox
              checked={isConfirmed}
              className="mb-4"
              labelPosition="center"
              onChange={() => setEnableIsConfirmed(!isConfirmed)}
            >
              <Text as="span">
                Saya mengerti bahwa Pegawai dapat mengajukan perubahan lagi
              </Text>
            </Checkbox>
            <div className="flex space-x-5">
              <Button
                color="white"
                size="lg"
                disabled={isLoadingSubmission}
                onClick={() => onClose?.()}
              >
                Kembali
              </Button>
              <Button
                color="blue"
                size="lg"
                isLoading={isLoadingSubmission}
                disabled={isLoadingSubmission || !isConfirmed}
                onClick={() => handleSubmit()}
              >
                Lanjut tolak
              </Button>
            </div>
          </>
        )}

        {activeContent === 'SUCCESS' && (
          <>
            <img
              src={assetUrl('/illustrations/img-files-document-failed.png')}
              width={84}
              height={84}
              alt=""
              className="mb-6"
            />
            <Text
              variant="display-sm"
              className="mb-4"
              id="appeal-rejection-dialog-title"
              as="h3"
            >
              Pengajuan perubahan ditolak
            </Text>
            <Text
              variant="body-lg"
              color="subdued"
              className="mb-6"
              id="appeal-rejection-dialog-description"
            >
              Anda tidak perlu mengulang hasil penilaian Anda.
            </Text>
            <Button size="lg" onClick={() => onClose?.()}>
              Saya mengerti
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
